import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useFinancialWarnings } from 'contexts/FinancialWarningsContext';
import { debounce } from 'lodash';
import { useGetCashToSupplierCalculation } from 'services/calculations/cashFlow';

import { createCashToSuppliersParams } from 'components/BusinessToolForm/components/CalculationsObserver/hooks/cashFlow/helper';
import { IFormContentTypes } from 'components/BusinessToolForm/components/CalculationsObserver/utils/form';
import { initialPeriodsTableRow, IPeriodsTableRow } from 'components/BusinessToolForm/constants/periods';
import {
  CURRENT_ASSETS_NAMES,
  INITIAL_ASSETS_NAMES,
  NON_CURRENT_ASSETS_NAMES,
} from 'components/BusinessToolForm/Sections/FinancialPlan/OpeningBalanceSheets/InitialAssetsForm/utils/form';
import {
  CURRENT_LIABILITIES_NAMES,
  INITIAL_LIABILITIES_NAMES,
} from 'components/BusinessToolForm/Sections/FinancialPlan/OpeningBalanceSheets/InitialLiabilitiesForm/utils/form';
import {
  initialAssetsFormPrefix,
  initialLiabilitiesFormPrefix,
} from 'components/BusinessToolForm/Sections/FinancialPlan/OpeningBalanceSheets/utils/form';
import { COST_OF_SALES_FORM_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/PlannedExpenses/CostOfSalesForm/utils/form';
import { GENERAL_AND_ADMINISTRATIVE_FORM_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/PlannedExpenses/GeneralAndAdministrativeForm/utils/form';
import { SALES_AND_MARKETING_FORM_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/PlannedExpenses/SalesAndMarketingForm/utils/form';
import {
  costOfSalesPrefix,
  generalAndAdministrativePrefix,
  salesAndMarketingPrefix,
} from 'components/BusinessToolForm/Sections/FinancialPlan/PlannedExpenses/utils/form';
import { NET_INCOME_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/ProfitAndLossProjection/components/FormSections/NetIncome/utils/form';
import { NET_SALES_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/ProfitAndLossProjection/components/FormSections/NetSalesForm/utils/form';
import { netIncomePrefix } from 'components/BusinessToolForm/Sections/FinancialPlan/ProfitAndLossProjection/utils/form';
import { netSalesPrefix } from 'components/BusinessToolForm/Sections/FinancialPlan/ProfitAndLossProjection/utils/form';
import { INVESTING_ACTIVITIES_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/DirectForm/DirectFormComponents/Sections/InvestingActivities/utils/form';
import { OPERATING_ACTIVITIES_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/DirectForm/DirectFormComponents/Sections/OperatingActivities/utils/form';
import { ADJUSTMENTS_FOR_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/InDirectForm/InDirectFormComponents/Sections/AdjustmentsForForm/utils/form';
import {
  adjustmentsForPrefix,
  investingActivitiesPrefix,
  operatingActivitiesPrefix,
} from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/utils/form';
import { GLOBAL_UNIT_NAME } from 'components/BusinessToolForm/Sections/Global/form';
import {
  sumForecastYearsTableRowValues,
  sumPpeOpeningBalanceControlLimit,
} from 'components/BusinessToolForm/utils/financialCalculations';

import { concatWithDot } from 'helpers/string';

const THRESHOLD = 1e-3;

const buildCashToSuppliersContent = (getValues: (name: string) => unknown): IFormContentTypes['cashFlowToSuppliers'] =>
  [
    getValues(GLOBAL_UNIT_NAME),
    getValues(
      concatWithDot(
        initialLiabilitiesFormPrefix,
        INITIAL_LIABILITIES_NAMES.CURRENT_LIABILITIES,
        CURRENT_LIABILITIES_NAMES.PAYMENT_DURATION_CREDIT_PURCHASES,
      ),
    ),
    getValues(
      concatWithDot(
        initialLiabilitiesFormPrefix,
        INITIAL_LIABILITIES_NAMES.CURRENT_LIABILITIES,
        CURRENT_LIABILITIES_NAMES.PERCENT_CREDIT_PURCHASES,
      ),
    ),
    getValues(
      concatWithDot(initialAssetsFormPrefix, INITIAL_ASSETS_NAMES.CURRENT_ASSETS, CURRENT_ASSETS_NAMES.INVENTORY_VALUE),
    ),
    getValues(
      concatWithDot(
        initialAssetsFormPrefix,
        INITIAL_ASSETS_NAMES.CURRENT_ASSETS,
        CURRENT_ASSETS_NAMES.INVENTORY_SIZE_AVG_MONTHLY_INCOME,
      ),
    ),
    getValues(concatWithDot(netSalesPrefix, NET_SALES_NAMES.COST_OF_SALES)),
    getValues(concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.CASH_PAID_EMPLOYEES)),
    getValues(concatWithDot(costOfSalesPrefix, COST_OF_SALES_FORM_NAMES.COST_OF_SALES_TABLE)),
    getValues(concatWithDot(salesAndMarketingPrefix, SALES_AND_MARKETING_FORM_NAMES.SALES_AND_MARKETING_TABLE)),
    getValues(
      concatWithDot(
        generalAndAdministrativePrefix,
        GENERAL_AND_ADMINISTRATIVE_FORM_NAMES.GENERAL_AND_ADMINISTRATIVE_TABLE,
      ),
    ),
    getValues(
      concatWithDot(
        initialLiabilitiesFormPrefix,
        INITIAL_LIABILITIES_NAMES.CURRENT_LIABILITIES,
        CURRENT_LIABILITIES_NAMES.TOTAL_ACCOUNTS_PAYABLE,
      ),
    ),
    getValues(concatWithDot(netIncomePrefix, NET_INCOME_NAMES.NON_OPERATING_EXPENSES)),
    getValues(
      concatWithDot(
        initialAssetsFormPrefix,
        INITIAL_ASSETS_NAMES.NON_CURRENT_ASSETS,
        NON_CURRENT_ASSETS_NAMES.INITIAL_COST_PPE,
      ),
    ),
    getValues(
      concatWithDot(
        initialAssetsFormPrefix,
        INITIAL_ASSETS_NAMES.NON_CURRENT_ASSETS,
        NON_CURRENT_ASSETS_NAMES.INTANGIBLE_ASSETS,
      ),
    ),
  ] as IFormContentTypes['cashFlowToSuppliers'];

export const useNonCurrentAssetsControlLimitWarnings = () => {
  const { t } = useTranslation();
  const { id: businessPlanId = '' } = useParams();
  const { getValues } = useFormContext();
  const getCashToSupplierCalculation = useGetCashToSupplierCalculation();
  const { intangibleAmortizationData, setIntangibleAmortizationData, ppeDepreciationData, setPpeDepreciationData } =
    useFinancialWarnings();

  const operationUnit = useWatch({ name: GLOBAL_UNIT_NAME });
  const costOfSalesTable = useWatch({
    name: concatWithDot(costOfSalesPrefix, COST_OF_SALES_FORM_NAMES.COST_OF_SALES_TABLE),
  });
  const salesAndMarketingTable = useWatch({
    name: concatWithDot(salesAndMarketingPrefix, SALES_AND_MARKETING_FORM_NAMES.SALES_AND_MARKETING_TABLE),
  });
  const generalAndAdministrativeTable = useWatch({
    name: concatWithDot(
      generalAndAdministrativePrefix,
      GENERAL_AND_ADMINISTRATIVE_FORM_NAMES.GENERAL_AND_ADMINISTRATIVE_TABLE,
    ),
  });
  const investingActivitiesTable = useWatch({
    name: concatWithDot(investingActivitiesPrefix, INVESTING_ACTIVITIES_NAMES.TABLE),
  });
  const gainsAndLossesFromAssetSales = useWatch({
    name: concatWithDot(adjustmentsForPrefix, ADJUSTMENTS_FOR_NAMES.GAIN_LOSSES_ASSET_SALES),
  });

  const intangibleAssets = useWatch({
    name: concatWithDot(
      initialAssetsFormPrefix,
      INITIAL_ASSETS_NAMES.NON_CURRENT_ASSETS,
      NON_CURRENT_ASSETS_NAMES.INTANGIBLE_ASSETS,
    ),
  });
  const initialCostPpe = useWatch({
    name: concatWithDot(
      initialAssetsFormPrefix,
      INITIAL_ASSETS_NAMES.NON_CURRENT_ASSETS,
      NON_CURRENT_ASSETS_NAMES.INITIAL_COST_PPE,
    ),
  });

  const [localAmortizationData, setLocalAmortizationData] = useState(intangibleAmortizationData);
  const [localDepreciationData, setLocalDepreciationData] = useState(ppeDepreciationData);

  useEffect(() => {
    if (intangibleAmortizationData) {
      setLocalAmortizationData(intangibleAmortizationData);
    }
  }, [intangibleAmortizationData]);

  useEffect(() => {
    if (ppeDepreciationData) {
      setLocalDepreciationData(ppeDepreciationData);
    }
  }, [ppeDepreciationData]);

  const debouncedFetchExpenseData = useMemo(
    () =>
      debounce(async () => {
        if (!businessPlanId) return;

        try {
          const content = buildCashToSuppliersContent(getValues);
          const { cashToSuppliersParams, unit } = createCashToSuppliersParams(content);
          const { amortization, depreciationFixedExpense } = await getCashToSupplierCalculation.mutateAsync({
            unit,
            businessPlanId,
            cashToSuppliersParams,
          });

          const amortizationRow = amortization as unknown as IPeriodsTableRow;
          const depreciationRow = depreciationFixedExpense as unknown as IPeriodsTableRow;
          setLocalAmortizationData(amortizationRow);
          setLocalDepreciationData(depreciationRow);
          setIntangibleAmortizationData(amortizationRow);
          setPpeDepreciationData(depreciationRow);
        } catch {
          // Keep previous limits when calculation is unavailable.
        }
      }, 800),
    [businessPlanId, getCashToSupplierCalculation, getValues, setIntangibleAmortizationData, setPpeDepreciationData],
  );

  useEffect(() => {
    debouncedFetchExpenseData();
    return () => debouncedFetchExpenseData.cancel();
  }, [
    costOfSalesTable,
    salesAndMarketingTable,
    generalAndAdministrativeTable,
    operationUnit,
    debouncedFetchExpenseData,
  ]);

  const amortizationData = localAmortizationData ?? intangibleAmortizationData;
  const depreciationData = localDepreciationData ?? ppeDepreciationData;

  const intangibleControlLimit = amortizationData ? sumForecastYearsTableRowValues(amortizationData) : 0;
  const intangibleEnteredValue = Number(intangibleAssets) || 0;
  const isIntangibleBelowControlLimit =
    intangibleControlLimit > THRESHOLD && intangibleEnteredValue + THRESHOLD < intangibleControlLimit;

  const proceedsFromSale = (investingActivitiesTable?.[2] as IPeriodsTableRow | undefined) ?? initialPeriodsTableRow;
  const governmentGrants = (investingActivitiesTable?.[4] as IPeriodsTableRow | undefined) ?? initialPeriodsTableRow;
  const gainsAndLosses = (gainsAndLossesFromAssetSales as IPeriodsTableRow | undefined) ?? initialPeriodsTableRow;

  const ppeControlLimit = depreciationData
    ? sumPpeOpeningBalanceControlLimit(depreciationData, proceedsFromSale, gainsAndLosses, governmentGrants)
    : 0;
  const ppeEnteredValue = Number(initialCostPpe) || 0;
  const isPpeBelowControlLimit = ppeControlLimit > THRESHOLD && ppeEnteredValue + THRESHOLD < ppeControlLimit;

  const fieldWarnings = useMemo(() => {
    const warnings: Record<string, string> = {};

    if (isIntangibleBelowControlLimit) {
      warnings[NON_CURRENT_ASSETS_NAMES.INTANGIBLE_ASSETS] = t(
        'financialPlan.openingBalanceSheets.initialAssets.nonCurrentAssets.intangibleAssetsBelowControlLimit',
        { limit: intangibleControlLimit.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
      );
    }

    if (isPpeBelowControlLimit) {
      warnings[NON_CURRENT_ASSETS_NAMES.INITIAL_COST_PPE] = t(
        'financialPlan.openingBalanceSheets.initialAssets.nonCurrentAssets.initialCostPpeBelowControlLimit',
        { limit: ppeControlLimit.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
      );
    }

    return Object.keys(warnings).length > 0 ? warnings : undefined;
  }, [intangibleControlLimit, isIntangibleBelowControlLimit, isPpeBelowControlLimit, ppeControlLimit, t]);

  return {
    fieldWarnings,
    intangibleControlLimit,
    ppeControlLimit,
    isIntangibleBelowControlLimit,
    isPpeBelowControlLimit,
  };
};

/** @deprecated Use useNonCurrentAssetsControlLimitWarnings */
export const useIntangibleAssetsControlLimitWarning = useNonCurrentAssetsControlLimitWarnings;
